import React, { PropsWithChildren, useState, Dispatch } from 'react'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ListItemIcon,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { StyledMenu } from '../../styles/PopupStyles'
import { Edit, Delete } from '@mui/icons-material'
import { Technology } from '../../store/types'
import { formatTechnologyType } from '../../store/utils'
import { useDeleteTechnologyMutation } from '../../store/redux/reducers/technologiesSlice'
import { useNotification } from '../../store/redux/hooks'
import { StyledButtonTechTile, StyledMenuItemAsButton } from '../../styles/ButtonStyles'
import { StyledContainerRowCenter } from '../../styles/ContainerStyles'
import EditTechnologyPopup from './EditTechnologyPopup'
import ConfirmButton from '../Button/ConfirmButton'

type TechnologyPopupProps = {
  technology: Technology
  isCollapsed: boolean
  menuOpen: boolean
  setMenuOpen: Dispatch<React.SetStateAction<boolean>>
}

const TechnologyPopup = ({
  children,
  technology,
  isCollapsed,
  menuOpen,
  setMenuOpen,
}: PropsWithChildren<TechnologyPopupProps>) => {
  const theme = useTheme()

  // TILE ELEMENT
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const [popupAnchorEl, setPopupAnchorEl] = useState<HTMLLIElement | null>(null)

  // TRANSLATION
  const { t } = useTranslation()

  // NOTIFICATIONS
  const { displayNotification } = useNotification()

  // DELETE DIALOG
  const [open, setOpen] = useState(false)
  const [deleteTechnology] = useDeleteTechnologyMutation()

  // EDIT POPUP
  const [editPopupOpen, setEditPopupOpen] = useState(false)

  const handleDelete = async () => {
    try {
      await deleteTechnology(technology.id).unwrap()
      displayNotification({ message: t('page.adminsPage.alerts.technology.delete.success'), type: 'success' })
    } catch (_) {
      displayNotification({
        message: t('page.adminsPage.alerts.technology.delete.error'),
        type: 'error',
      })
    }
  }

  // OPEN MENU
  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setMenuOpen(true)
  }

  // CLOSE MENU
  const handleClose = () => {
    setMenuOpen(false)
  }

  const handleEditClick = (event: React.MouseEvent<HTMLLIElement>) => {
    setPopupAnchorEl(event.currentTarget)
    setEditPopupOpen(true)
  }

  const tooltip = (
    <Box>
      <Typography variant={'body2'} fontWeight={500}>
        {technology.name}
      </Typography>
      <Typography variant={'caption'}>{formatTechnologyType(technology.type)}</Typography>
    </Box>
  )

  return (
    <>
      <PopupState variant="popover" popupId="demo-popup-menu" parentPopupState={null} disableAutoFocus={false}>
        {popupState => {
          const menuProps = {
            ...bindMenu(popupState),
            open: menuOpen,
            anchorEl,
            onClose: handleClose,
            anchorOrigin: {
              vertical: 'bottom' as const,
              horizontal: 'right' as const,
            },
            transformOrigin: {
              vertical: 'top' as const,
              horizontal: 'left' as const,
            },
            sx: {
              display: 'flex',
              flexDirection: 'column',
              top: '-25px',
              left: '-15px',
            },
          }

          return (
            <>
              <Tooltip title={isCollapsed ? tooltip : ''} placement="right-end">
                <StyledButtonTechTile {...bindTrigger(popupState)} onClick={handleOpen}>
                  {children}
                </StyledButtonTechTile>
              </Tooltip>

              <StyledMenu {...menuProps} autoFocus role="menu">
                <StyledContainerRowCenter
                  key="header"
                  sx={{ gap: 1, py: 1, px: 2 }}
                  aria-hidden="true"
                  tabIndex={-1}
                  role="presentation"
                >
                  <Typography variant="body2" fontWeight={700}>
                    {technology.name}
                  </Typography>
                  <Typography variant="body2" role="presentation">
                    •
                  </Typography>
                  <Typography variant="body2" role="presentation">
                    {formatTechnologyType(technology.type)}
                  </Typography>
                </StyledContainerRowCenter>
                <Divider key="divider" sx={{ my: 0.5 }} role="presentation" />
                <StyledMenuItemAsButton
                  key="edit"
                  onClick={handleEditClick}
                  aria-label={t('components.popover.edit')}
                  role="menuitem"
                >
                  <ListItemIcon>
                    <Edit sx={{ width: 16 }} />
                  </ListItemIcon>
                  <Typography variant="body2">{t('components.popover.edit')}</Typography>
                </StyledMenuItemAsButton>
                <StyledMenuItemAsButton
                  key="delete"
                  onClick={() => setOpen(true)}
                  aria-label={t('components.popover.delete')}
                  role="menuitem"
                >
                  <ListItemIcon>
                    <Delete sx={{ width: 16 }} />
                  </ListItemIcon>
                  <Typography variant="body2">{t('components.popover.delete')}</Typography>
                </StyledMenuItemAsButton>
              </StyledMenu>

              <Dialog
                onClose={() => setOpen(false)}
                open={open}
                PaperProps={{
                  style: {
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(2),
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(2),
                  },
                }}
              >
                <DialogTitle>{t('page.adminsPage.dialog.delete.title')}</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    <Trans i18nKey="page.adminsPage.dialog.delete.content" values={{ name: technology.name }} />
                    <br />
                    <Typography component="span" sx={{ fontStyle: 'italic', pt: 3, color: theme.palette.warning.main }}>
                      {t('page.adminsPage.dialog.delete.warning')}
                    </Typography>
                  </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ gap: 2 }}>
                  <Button size="small" onClick={() => setOpen(false)}>{t('page.adminsPage.dialog.return')}</Button>
                  <ConfirmButton text={t('page.adminsPage.dialog.delete.confirm')} onClick={handleDelete}/>
                </DialogActions>
              </Dialog>
            </>
          )
        }}
      </PopupState>

      <EditTechnologyPopup
        technology={technology}
        open={editPopupOpen}
        onClose={() => setEditPopupOpen(false)}
        anchorEl={popupAnchorEl}
      />
    </>
  )
}

export default TechnologyPopup